import { useAuth } from "@/hooks/useAuth";
import { useTranslation } from "react-i18next";
import { useLocation } from "wouter";
import { AlertTriangle, CreditCard, Settings } from "lucide-react";
import { useMutation } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useBillingProvider } from "@/hooks/useBillingProvider";

const GRACE_PERIOD_DAYS = 7;

export function PaymentFailedBanner() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [location, setLocation] = useLocation();
  const { showsStripeBilling } = useBillingProvider();

  // Hook przed jakimkolwiek early-return (rules-of-hooks; review #7) — kolejność
  // hooków musi być stała między renderami.
  const portalMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/billing/portal");
      const data = res as { url?: string };
      return data.url as string;
    },
    onSuccess: (url: string) => {
      window.location.href = url;
    },
  });

  const pastDue = user?.pastDue === true;
  const paymentFailedAt: string | null = user?.paymentFailedAt ?? null;

  // VP-329: baner prowadzi do portalu Stripe, którego konto rozliczane przez Shopify
  // nie ma prawa zobaczyć (§1.2.1). Nieopłacony plan Shopify obsługuje sam Shopify.
  if (!pastDue || location === "/settings" || !showsStripeBilling) return null;

  let daysRemaining = GRACE_PERIOD_DAYS;
  if (paymentFailedAt) {
    const elapsed = Math.floor(
      (Date.now() - new Date(paymentFailedAt).getTime()) / (1000 * 60 * 60 * 24)
    );
    daysRemaining = Math.max(0, GRACE_PERIOD_DAYS - elapsed);
  }

  return (
    <div className="w-full bg-amber-600 text-white text-sm flex items-center justify-center gap-3 py-2 px-4 z-50 flex-shrink-0">
      <AlertTriangle className="w-4 h-4 flex-shrink-0" />
      <span>{t("paymentFailedBanner.message", { count: daysRemaining })}</span>
      <button
        onClick={() => portalMutation.mutate()}
        disabled={portalMutation.isPending}
        className="ml-2 bg-white/20 hover:bg-white/30 text-white px-3 py-0.5 rounded text-xs font-semibold transition-colors disabled:opacity-50 flex items-center gap-1"
      >
        <CreditCard className="w-3 h-3" />
        {t("paymentFailedBanner.updatePayment")}
      </button>
      <button
        onClick={() => setLocation("/settings")}
        className="bg-white/10 hover:bg-white/20 text-white/80 px-3 py-0.5 rounded text-xs font-semibold transition-colors flex items-center gap-1"
      >
        <Settings className="w-3 h-3" />
        {t("paymentFailedBanner.goToSettings")}
      </button>
    </div>
  );
}
